import React, { useState, useEffect, useLayoutEffect, useContext, useMemo, useRef, useCallback } from "react";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import Loader from "../../components/loader";
import "./styles.css";

// Страница «Мониторинг»: график нагрузки одного устройства (CPU/RAM/SWAP, %) за выбранный
// диапазон — читает device_performances через ApiDevice.Performance (минутные снимки,
// panel:metrics-snapshot). Масштаб «до 1 дня» — данные старше 3 суток чистит
// panel:clean-device-metrics, дальше диапазона всё равно смотреть нечего.
const RANGES = [
    { hours: 1, label: '1ч' },
    { hours: 6, label: '6ч' },
    { hours: 24, label: '24ч' },
];

// Порядок и цвета серий ФИКСИРОВАНЫ (не переприсваиваются по алфавиту/фильтру) — валидированная
// категориальная тройка (see dataviz skill: slots 1/2/3, all-pairs CVD-safe в обоих режимах).
const SERIES = [
    { key: 'cpu', label: 'CPU', color: '#3987e5' },
    { key: 'ram', label: 'RAM', color: '#d95926' },
    { key: 'swap', label: 'Swap', color: '#199e70' },
];

export default function MonitoringIndex({ ...other }) {
    return <Page {...other}>
        <MonitoringIndexContent/>
    </Page>;
}

function MonitoringIndexContent() {
    const contextContent = useContext(ContextContent);
    const [_devices, set_devices] = useState(null); // null — список ещё грузится (докачивается постранично)
    const [_deviceId, set_deviceId] = useState('');
    const [_hours, set_hours] = useState(24);
    const [_points, set_points] = useState(null); // null — ещё грузится
    const requestSeq = useRef(0);

    useLayoutEffect(() => {
        contextContent.setTitle('Мониторинг');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Список устройств — один раз, для выбора. compact-поля (id/name/status) уже даёт index;
    // сам он пагинирован по 20 (см. DevicesController::index, per_page не настраивается) —
    // докачиваем остальные страницы через getResponseNext(), как и остальные полные списки.
    useEffect(() => {
        (async () => {
            const api = ApiDevice.Index();
            let list = (await api.getResponse()) ?? [];
            while (api.paginationCurrent && api.paginationLast && api.paginationCurrent < api.paginationLast) {
                const more = await api.getResponseNext();
                if (!more) break;
                list = list.concat(more);
            }
            set_devices(list);
            if (list.length > 0) set_deviceId(String(list[0].id));
        })();
    }, []);

    const load = useCallback(async (deviceId, hours) => {
        if (!deviceId) return;
        const seq = ++requestSeq.current;
        set_points(null);
        const data = await ApiDevice.Performance(deviceId, hours).getResponse();
        if (seq !== requestSeq.current) return; // ответ на устаревший запрос (сменили выбор) — игнор
        set_points(data ?? []);
    }, []);

    useEffect(() => {
        load(_deviceId, _hours);
    }, [_deviceId, _hours, load]);

    const selectedDevice = useMemo(
        () => (_devices ?? []).find((d) => String(d.id) === String(_deviceId)) ?? null,
        [_devices, _deviceId],
    );

    return <div className="monitoring">
        <div className="monitoring__controls">
            <select
                className="monitoring__device-select"
                value={_deviceId}
                onChange={(e) => set_deviceId(e.target.value)}
            >
                {(_devices ?? []).map((d) => (
                    <option key={d.id} value={d.id}>{d.name} (#{d.id})</option>
                ))}
            </select>

            <div className="monitoring__ranges">
                {RANGES.map((r) => (
                    <button
                        key={r.hours}
                        type="button"
                        className={`monitoring__range${_hours === r.hours ? ' monitoring__range_active' : ''}`}
                        onClick={() => set_hours(r.hours)}
                    >
                        {r.label}
                    </button>
                ))}
            </div>
        </div>

        {/* Список устройств докачивается постранично и на большой ферме идёт заметно —
            без индикатора всё это время страница уверяла, что устройств нет вовсе. */}
        {_devices === null ? (
            <Loader text="Загрузка устройств…"/>
        ) : _devices.length === 0 ? (
            <div className="monitoring__empty">Устройств пока нет</div>
        ) : _points === null ? (
            <Loader/>
        ) : _points.length === 0 ? (
            <div className="monitoring__empty">Нет данных за этот период — метрики снимаются раз в минуту, для новых/недавно активных устройств их может ещё не быть</div>
        ) : (
            <LoadChart points={_points} deviceName={selectedDevice?.name ?? ''}/>
        )}
    </div>;
}

// SVG-график: 3 линии (CPU/RAM/Swap, %), общая ось 0–100 (одна шкала — все три величины в
// одних единицах, второй оси не заводим, см. dataviz skill "one axis"). Свой ховер:
// вертикальная линия-курсор + тултип на ближайшую по времени точку.
const CHART_W = 1000;
const CHART_H = 280;
const PAD_L = 34;   // место под подписи оси Y
const PAD_B = 22;   // место под подписи оси X

function LoadChart({ points, deviceName }) {
    const svgRef = useRef(null);
    const [_hoverIndex, set_hoverIndex] = useState(null);

    const times = useMemo(() => points.map((p) => new Date(p.t).getTime()), [points]);
    const minT = times[0];
    const maxT = times[times.length - 1];
    const spanT = Math.max(1, maxT - minT);

    const innerW = CHART_W - PAD_L;
    const innerH = CHART_H - PAD_B;

    const xOf = (t) => PAD_L + ((t - minT) / spanT) * innerW;
    const yOf = (v) => innerH - (Math.max(0, Math.min(100, v)) / 100) * innerH;

    const paths = useMemo(() => SERIES.map((series) => {
        let d = '';
        points.forEach((point, i) => {
            const value = point[series.key];
            if (value === null || value === undefined) return; // разрыв линии — точку пропускаем
            const cmd = d === '' ? 'M' : 'L';
            d += `${cmd}${xOf(times[i]).toFixed(1)},${yOf(value).toFixed(1)} `;
        });
        return { ...series, d };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [points, times, minT, spanT]);

    const handleMove = (e) => {
        const rect = svgRef.current.getBoundingClientRect();
        const svgX = ((e.clientX - rect.left) / rect.width) * CHART_W;
        const t = minT + ((svgX - PAD_L) / innerW) * spanT;
        // ближайшая по времени точка — линейный поиск (точек максимум ~1440, дёшево)
        let best = 0, bestDiff = Infinity;
        times.forEach((candidate, i) => {
            const diff = Math.abs(candidate - t);
            if (diff < bestDiff) { bestDiff = diff; best = i; }
        });
        set_hoverIndex(best);
    };

    const hover = _hoverIndex !== null ? points[_hoverIndex] : null;
    const hoverX = _hoverIndex !== null ? xOf(times[_hoverIndex]) : null;

    const gridLines = [0, 25, 50, 75, 100];
    // Подписи оси X: 5 равномерных отметок времени по диапазону данных.
    const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => minT + f * spanT);
    const timeLabel = (ms) => new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

    return <div className="monitoring__chart-wrap">
        <div className="monitoring__legend">
            {SERIES.map((s) => (
                <span key={s.key} className="monitoring__legend-item">
                    <span className="monitoring__legend-dot" style={{ background: s.color }}/>
                    {s.label}
                </span>
            ))}
            {deviceName ? <span className="monitoring__legend-device">{deviceName}</span> : null}
        </div>

        <svg
            ref={svgRef}
            className="monitoring__svg"
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            preserveAspectRatio="none"
            onMouseMove={handleMove}
            onMouseLeave={() => set_hoverIndex(null)}
        >
            {gridLines.map((pct) => (
                <g key={pct}>
                    <line
                        x1={PAD_L} x2={CHART_W} y1={yOf(pct)} y2={yOf(pct)}
                        className="monitoring__gridline"
                    />
                    <text x={PAD_L - 6} y={yOf(pct) + 3} className="monitoring__axis-label" textAnchor="end">
                        {pct}
                    </text>
                </g>
            ))}

            {xTicks.map((t, i) => (
                <text
                    key={i}
                    x={Math.min(CHART_W - 4, Math.max(PAD_L, xOf(t)))}
                    y={CHART_H - 4}
                    className="monitoring__axis-label"
                    textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
                >
                    {timeLabel(t)}
                </text>
            ))}

            {paths.map((p) => (
                <path key={p.key} d={p.d} className="monitoring__line" style={{ stroke: p.color }}/>
            ))}

            {hoverX !== null ? (
                <line x1={hoverX} x2={hoverX} y1={0} y2={innerH} className="monitoring__crosshair"/>
            ) : null}
            {hoverX !== null ? SERIES.map((s) => {
                const value = hover[s.key];
                if (value === null || value === undefined) return null;
                return <circle key={s.key} cx={hoverX} cy={yOf(value)} r="3.5" style={{ fill: s.color }}/>;
            }) : null}
        </svg>

        {hover ? (
            <div
                className="monitoring__tooltip"
                style={{ left: `${(hoverX / CHART_W) * 100}%` }}
            >
                <div className="monitoring__tooltip-time">{new Date(hover.t).toLocaleString('ru-RU')}</div>
                {SERIES.map((s) => {
                    const value = hover[s.key];
                    return <div key={s.key} className="monitoring__tooltip-row">
                        <span className="monitoring__legend-dot" style={{ background: s.color }}/>
                        {s.label}: <b>{value === null || value === undefined ? '—' : `${value}%`}</b>
                        {s.key === 'ram' && hover.ram_usage_mb != null
                            ? <span className="monitoring__tooltip-sub"> ({hover.ram_usage_mb} / {hover.ram_total_mb} МБ)</span>
                            : null}
                    </div>;
                })}
            </div>
        ) : null}
    </div>;
}
